import React, { useState, useEffect, useRef } from 'react';
import { assetUrl } from '../utils/assetUrl';
import { useLanguage } from '../context/LanguageContext';
import { PROJECT_CONFIG } from '../config/projectConfig';
import audioService from '../services/audioService';
import './TempleHeader.css';

// Single-line temple name shown in the mobile header. The title swaps to
// match the currently selected interface language so mobile users always
// see the temple name in the script they're reading.
const TEMPLE_TITLES = {
  mr: 'श्री मंगलदेव ग्रह मंदिर, अमळनेर',
  hi: 'श्री मंगलदेव ग्रह मंदिर, अमलनेर',
  en: 'Shri Mangal Dev Grah Mandir, Amalner',
};

const ACTIONS = [
  {
    key: 'reset',
    labels: { mr: 'पुन्हा सुरू', en: 'Reset', hi: 'रीसेट' },
    color: '#60a5fa',
    prop: 'onReset',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
        <path d="M3 3v5h5"/>
      </svg>
    ),
  },
  {
    key: 'info',
    labels: { mr: 'माहिती', en: 'Info', hi: 'जानकारी' },
    color: '#fbbf24',
    prop: 'onInfo',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 16v-4"/>
        <path d="M12 8h.01"/>
      </svg>
    ),
  },
  {
    key: 'live',
    labels: { mr: 'थेट दर्शन', en: 'Live Darshan', hi: 'लाइव दर्शन' },
    color: '#ef4444',
    prop: 'onLive',
    icon: (
      // Filled red circle with a "LIVE" hint — read instantly as the
      // ongoing live aarti stream button.
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none"/>
        <path d="M5.6 5.6a9 9 0 0 0 0 12.8"/>
        <path d="M18.4 5.6a9 9 0 0 1 0 12.8"/>
        <path d="M2.5 2.5a13 13 0 0 0 0 19"/>
        <path d="M21.5 2.5a13 13 0 0 1 0 19"/>
      </svg>
    ),
  },
  {
    key: 'links',
    labels: { mr: 'व्हिडिओ', en: 'Videos', hi: 'वीडियो' },
    color: '#f87171',
    prop: 'onLinks',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/>
        <path d="m10 15 5-3-5-3z"/>
      </svg>
    ),
  },
  {
    key: 'vr',
    labels: { mr: 'व्हीआर', en: 'Enter VR', hi: 'वीआर' },
    color: '#a78bfa',
    prop: 'onVR',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="10" rx="3"/>
        <circle cx="8" cy="12" r="1.6" fill="currentColor" stroke="none"/>
        <circle cx="16" cy="12" r="1.6" fill="currentColor" stroke="none"/>
      </svg>
    ),
  },
];

const TempleHeader = ({ language: langProp, onReset, onInfo, onLinks, onLive }) => {
  const { language: ctxLang, setLanguage } = useLanguage();
  const language = langProp || ctxLang || 'mr';
  const [droneEditing, setDroneEditing] = useState(false);
  const [langExpanded, setLangExpanded] = useState(false);
  const [minimal, setMinimal] = useState(false);
  const [isLiveStreamActive, setIsLiveStreamActive] = useState(false);
  const [muted, setMuted] = useState(() => {
    try { return !!audioService.isMuted?.(); } catch { return false; }
  });
  // True once the ambient music BufferSource is actually producing
  // sound. Until then the header equalizer bars stay static so the
  // animation only kicks in when audio is genuinely audible.
  const [audioPlaying, setAudioPlaying] = useState(() => {
    try { return !!audioService.isAmbientPlaying?.(); } catch { return false; }
  });
  // WebXR support — when true, the "Enter VR" action button is rendered
  // alongside Info / Live / Reset. Detection runs once on mount.
  const [vrSupported, setVrSupported] = useState(false);
  const [vrHint, setVrHint] = useState('');
  const vrHintTimer = useRef(null);
  const langRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (typeof navigator === 'undefined' || !navigator.xr) return;
        if (!window.isSecureContext) return;
        const ok = await navigator.xr.isSessionSupported('immersive-vr');
        if (!cancelled) setVrSupported(!!ok);
      } catch { /* unsupported */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const showVrHint = (msg) => {
    setVrHint(msg);
    if (vrHintTimer.current) clearTimeout(vrHintTimer.current);
    vrHintTimer.current = setTimeout(() => setVrHint(''), 4500);
  };

  const handleEnterVR = async () => {
    // Re-verify on click — some browsers only resolve after a gesture.
    let ok = vrSupported;
    try {
      if (navigator.xr) ok = await navigator.xr.isSessionSupported('immersive-vr');
    } catch { /* fall through */ }
    if (!ok) {
      showVrHint('WebXR not available. Open this page in the Quest Browser, or connect via Quest Link / Air Link and use desktop Chrome/Edge.');
      return;
    }
    // Click into Matterport's hidden ".view-in-vr" element so the
    // official WebXR pipeline runs (full scene, controllers, eye-comfort).
    try {
      const iframe = document.querySelector('iframe[title="Matterport"]');
      const doc = iframe?.contentDocument;
      const btn = doc?.querySelector('.view-in-vr')
        || doc?.querySelector('[class*="view-in-vr"]');
      if (btn) {
        btn.click();
      } else {
        showVrHint('Matterport VR control not ready yet — try again in a moment.');
      }
    } catch {
      showVrHint('Could not start VR session.');
    }
  };

  const handlers = { onReset, onInfo, onLinks, onLive, onVR: handleEnterVR };

  // Toggle the global minimal-UI class on <body>. CSS hides all header/footer
  // chrome while it's on; a small floating title pill replaces them.
  useEffect(() => {
    document.body.classList.toggle('minimal-ui', minimal);
    return () => document.body.classList.remove('minimal-ui');
  }, [minimal]);

  useEffect(() => {
    const handler = (e) => setDroneEditing(e.detail?.active ?? false);
    window.addEventListener('droneEditMode', handler);
    return () => window.removeEventListener('droneEditMode', handler);
  }, []);

  useEffect(() => {
    const onMuteChange = (e) => setMuted(!!e.detail?.muted);
    window.addEventListener('audioMuted', onMuteChange);
    return () => window.removeEventListener('audioMuted', onMuteChange);
  }, []);

  useEffect(() => {
    const onAudioPlaying = (e) => {
      if (e.detail?.source && e.detail.source !== 'ambient') return;
      setAudioPlaying(!!e.detail?.playing);
    };
    window.addEventListener('audioPlaying', onAudioPlaying);
    return () => window.removeEventListener('audioPlaying', onAudioPlaying);
  }, []);

  useEffect(() => {
    const handler = (e) => setIsLiveStreamActive(!!e.detail);
    window.addEventListener('liveStreamActive', handler);
    return () => window.removeEventListener('liveStreamActive', handler);
  }, []);

  const handleMuteToggle = () => {
    const next = !muted;
    setMuted(next);
    try { audioService.setMuted(next); } catch { /* no-op */ }
  };

  // Close the expanded language chip when tapping outside
  useEffect(() => {
    if (!langExpanded) return;
    const onDocDown = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangExpanded(false);
      }
    };
    document.addEventListener('pointerdown', onDocDown);
    return () => document.removeEventListener('pointerdown', onDocDown);
  }, [langExpanded]);

  const supportedLangs = PROJECT_CONFIG.i18n.supportedLanguages;

  const getLanguageLabel = (code) => {
    const lang = supportedLangs.find((l) => l.code === code);
    return lang ? lang.label : code.toUpperCase();
  };

  // eslint-disable-next-line no-unused-vars
  const getLanguageAbbr = (code) => {
    const lang = supportedLangs.find((l) => l.code === code);
    return lang ? lang.abbr : code.toUpperCase();
  };

  const handleLangSelect = (code) => {
    setLanguage(code);
    setLangExpanded(false);
  };

  // ── Kiosk lock (Windows kiosk + online deployments) ─────────────────
  // Tap the header background (NOT the buttons / logo / language pills)
  // 5× within 3 seconds to open a PIN prompt. Entering PIN `1191`
  // toggles browser fullscreen and a "kiosk" flag that re-requests
  // fullscreen if the user dismisses it (Esc / F11). Repeat the 5-tap
  // + PIN to release the lock.
  const KIOSK_PIN = '1191';
  const [kioskActive, setKioskActive] = useState(() => {
    try { return localStorage.getItem('kioskMode') === '1'; } catch { return false; }
  });
  const [pinOpen, setPinOpen] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const tapTimes = useRef([]);
  const headerRef = useRef(null);

  const onHeaderTap = (e) => {
    // Ignore taps on interactive descendants — we only count taps on
    // the header background / temple-name area.
    const t = e.target;
    if (t && t.closest && t.closest('a,button,input,select,textarea,[role="button"]')) {
      return;
    }
    const now = Date.now();
    const recent = tapTimes.current.filter((t0) => now - t0 < 3000);
    recent.push(now);
    tapTimes.current = recent;
    if (recent.length >= 5) {
      tapTimes.current = [];
      setPinValue('');
      setPinError('');
      setPinOpen(true);
    }
  };

  const requestFullscreen = async () => {
    const el = document.documentElement;
    try {
      if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el.msRequestFullscreen) el.msRequestFullscreen();
    } catch { /* user gesture required — handled in PIN submit */ }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    } catch { /* noop */ }
  };

  // Keep the browser in fullscreen while kiosk mode is on. When the
  // user (or OS) exits fullscreen, re-request after a short delay.
  // NOTE: Browsers always allow Esc/F11 to leave fullscreen — true
  // OS-level kiosk lockdown must be configured on Windows (Assigned
  // Access / kiosk-mode launch flags) on top of this.
  useEffect(() => {
    if (!kioskActive) return undefined;
    let timer = null;
    const onChange = () => {
      const fsEl = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.msFullscreenElement;
      if (!fsEl && kioskActive) {
        // Browsers always allow Esc/F11 to exit fullscreen — we cannot
        // block that natively. Instead, when fullscreen exits while the
        // kiosk lock is on, open the PIN dialog and re-enter fullscreen
        // automatically after a short delay. The user must enter the
        // PIN to actually unlock.
        if (timer) clearTimeout(timer);
        setPinValue('');
        setPinError('');
        setPinOpen(true);
        timer = setTimeout(() => {
          requestFullscreen().catch(() => {});
        }, 400);
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('msfullscreenchange', onChange);
    // Block common shortcuts that exit fullscreen / open dev tools /
    // reload. Esc is included for completeness — browsers still exit
    // fullscreen natively, but the fullscreenchange handler above
    // catches that and re-prompts for the PIN.
    const onKey = (e) => {
      if (!kioskActive) return;
      if (e.key === 'Escape' || e.key === 'F11' || e.key === 'F12'
        || (e.ctrlKey && (e.key === 'r' || e.key === 'R'))
        || (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C'))) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('msfullscreenchange', onChange);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [kioskActive]);

  const submitPin = async () => {
    if (pinValue !== KIOSK_PIN) {
      setPinError('Incorrect PIN');
      setPinValue('');
      return;
    }
    const next = !kioskActive;
    setKioskActive(next);
    try {
      if (next) localStorage.setItem('kioskMode', '1');
      else localStorage.removeItem('kioskMode');
    } catch { /* ignore storage failures */ }
    setPinOpen(false);
    setPinValue('');
    setPinError('');
    if (next) await requestFullscreen();
    else await exitFullscreen();
  };

  return (
    <>
      {/* ── Top bar ── */}
      <header className="temple-header" ref={headerRef} onClick={onHeaderTap}>
        {/* Left: Mandir logo */}
        <a
          href="https://www.mangalagrahmandir.in/"
          target="_blank"
          rel="noopener noreferrer"
          className="temple-header__logo-left"
          aria-label="Shri Mangal Dev Grah Mandir website"
        >
          <img
            src={assetUrl('/assets/images/mandir-logo.png')}
            alt="मंगलग्रह मंदिर"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </a>

        {/* Center: Temple name */}
        <div className="temple-header__center">
          <div className="temple-header__name-devanagari">
            श्री मंगलदेव ग्रह मंदिर, अमळनेर
          </div>
          <div className="temple-header__name-english">
            Shri Mangal Dev Grah Mandir, Amalner
          </div>
        </div>

        {/* Mobile-only single-language title — swaps with the active
            language. Hidden on desktop where temple-header__center renders
            the bilingual name. */}
        <div className="temple-header__name-mobile" lang={language}>
          {TEMPLE_TITLES[language] || TEMPLE_TITLES.en}
        </div>

        {/* Right: spacer to keep title visually centered (fullscreen toggle
             is rendered as a fixed floating button so its position is
             identical in both normal and minimal views). */}
        <div className="temple-header__right" aria-hidden="true" />
      </header>

      {/* ═══════════════════════════════════════════════════════════════════
          MOBILE UI: Floating clusters (language selector + mobile buttons)
          These replace desktop header controls on mobile (max-width: 600px)
          ═══════════════════════════════════════════════════════════════════ */}

      {/* ── MOBILE: Left floating cluster — language selector ── */}
      <div
        ref={langRef}
        className={`mob-float-cluster mob-float-cluster--left${langExpanded ? ' mob-float-cluster--open' : ''}`}
        style={droneEditing || isLiveStreamActive ? { opacity: 0, pointerEvents: 'none' } : {}}
      >
        {/* Trigger pill */}
        <button
          type="button"
          className="mob-float-trigger"
          onClick={() => { setLangExpanded(e => !e); }}
          aria-label="Select language"
          aria-expanded={langExpanded}
          title={getLanguageLabel(language)}
        >
          <span className="mob-float-trigger__label">
            {(supportedLangs.find((l) => l.code === language)?.displayLabel)
              || (supportedLangs.find((l) => l.code === language)?.label)
              || language.toUpperCase()}
          </span>
        </button>

        {/* Expanded lang options */}
        {langExpanded && (
          <div className="mob-float-cluster__panel">
            {supportedLangs.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`mob-float-lang-opt${lang.code === language ? ' active' : ''}`}
                onClick={() => handleLangSelect(lang.code)}
              >
                {lang.displayLabel || lang.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── MOBILE: Right vertical button stack — mobile action buttons ── */}
      <div
        className="mob-button-stack"
        style={droneEditing || isLiveStreamActive ? { opacity: 0, pointerEvents: 'none' } : {}}
      >
        {/* Mute button — when audio is playing, shows animated EQ bars
            next to the speaker icon (matches the desktop AudioNowPlaying
            "now playing" indicator). */}
        <button type="button" className={`mob-stack-btn mob-stack-btn--audio${muted ? ' mob-stack-btn--muted' : ''}`} onClick={() => { handleMuteToggle(); }} aria-label={muted ? 'Unmute' : 'Mute'} title={muted ? 'Unmute' : 'Mute'}>
          {muted ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <line x1="23" y1="9" x2="17" y2="15"/>
              <line x1="17" y1="9" x2="23" y2="15"/>
            </svg>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              <span className="mob-stack-btn__eq" aria-hidden="true">
                <span className="mob-stack-btn__eq-bar" />
                <span className="mob-stack-btn__eq-bar" />
                <span className="mob-stack-btn__eq-bar" />
              </span>
            </>
          )}
        </button>

        {/* Live Darshan button — text label on mobile chip */}
        <button type="button" className="mob-stack-btn mob-stack-btn--live mob-stack-btn--text" onClick={() => { onLive?.(); }} aria-label="Live Darshan" title="Live Darshan">
          <span className="mob-stack-btn__label">Live</span>
        </button>

        {/* Info button — text label on mobile chip */}
        <button type="button" className="mob-stack-btn mob-stack-btn--text" onClick={() => { onInfo?.(); }} aria-label="Info" title="Info">
          <span className="mob-stack-btn__label">Info</span>
        </button>
      </div>

      {/* Static fullscreen / minimal-view toggle — same screen position
          regardless of minimal state, so the user always knows where to find it. */}
      <button
        type="button"
        className={`fullscreen-toggle-btn${minimal ? ' fullscreen-toggle-btn--active' : ''}`}
        onClick={() => setMinimal(m => !m)}
        aria-label={minimal ? 'Exit minimal view' : 'Enter minimal view'}
        aria-pressed={minimal}
        title={minimal ? 'Exit minimal view' : 'Minimal view'}
      >
        {minimal ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 4H5a1 1 0 0 0-1 1v4"/>
            <path d="M15 4h4a1 1 0 0 1 1 1v4"/>
            <path d="M9 20H5a1 1 0 0 1-1-1v-4"/>
            <path d="M15 20h4a1 1 0 0 0 1-1v-4"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 9V5a1 1 0 0 1 1-1h4"/>
            <path d="M20 9V5a1 1 0 0 0-1-1h-4"/>
            <path d="M4 15v4a1 1 0 0 0 1 1h4"/>
            <path d="M20 15v4a1 1 0 0 1-1 1h-4"/>
          </svg>
        )}
      </button>

      {/* ── Left header panel: language selector only ── */}
      <div
        className="temple-header-controls temple-header-controls--left"
        style={droneEditing ? { opacity: 0, pointerEvents: 'none', transition: 'opacity 0.2s' } : { transition: 'opacity 0.2s' }}
      >
        {supportedLangs.map((lang) => (
          <button
            key={lang.code}
            type="button"
            className={`temple-header-ctrl temple-header-ctrl--lang${lang.code === language ? ' is-active' : ''}`}
            onClick={() => handleLangSelect(lang.code)}
            aria-label={`Switch to ${lang.label}`}
          >
            {lang.label}
          </button>
        ))}
      </div>

      {/* ── Right header panel: action buttons + mute ── */}
      <div
        className="temple-header-controls"
        style={droneEditing ? { opacity: 0, pointerEvents: 'none', transition: 'opacity 0.2s' } : { transition: 'opacity 0.2s' }}
      >
        {/* Info, Live / Links, VR (if supported), Reset — order matches the spec */}
        {['info', 'live', 'links', 'vr', 'reset'].map((key) => {
          const def = ACTIONS.find(a => a.key === key);
          if (!def) return null;
          // Only render the VR action when WebXR is available.
          if (key === 'vr' && !vrSupported) return null;
          const handler = handlers[def.prop];
          if (!handler) return null;
          return (
            <button
              key={def.key}
              type="button"
              className="temple-header-ctrl temple-header-ctrl--action"
              onClick={handler}
              aria-label={def.labels[language] || def.labels.en}
              title={def.labels[language] || def.labels.en}
            >
              <span className="temple-header-ctrl__icon">{def.icon}</span>
              <span className="temple-header-ctrl__label">
                {def.labels[language] || def.labels.en}
              </span>
            </button>
          );
        })}

        <span className="temple-header-ctrl__sep" aria-hidden="true" />

        {/* 7: Mute — with "music playing" equalizer animation while unmuted
             AND the ambient source is actually producing sound. Before
             playback truly begins, bars render in a static `is-idle` state. */}
        <button
          type="button"
          className={`temple-header-ctrl temple-header-ctrl--mute${
            muted ? ' is-muted' : (audioPlaying ? ' is-playing' : ' is-idle')
          }`}
          onClick={handleMuteToggle}
          aria-label={muted ? 'Unmute audio' : 'Mute audio'}
          aria-pressed={muted}
          title={muted ? 'Unmute' : 'Mute'}
        >
          <span className="temple-header-ctrl__icon">
            {muted ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              </svg>
            )}
          </span>
          {/* EQ always rendered so the button width never changes;
              visibility is toggled via the `is-muted` class in CSS. */}
          <span className="temple-header-ctrl__eq" aria-hidden="true">
            <i /><i /><i />
          </span>
        </button>
      </div>

      {/* ── Bottom-right Innpact credit ── */}
      <div className="innpact-credit">
        Developed by
        <a href="https://innpactsolutions.com/" target="_blank" rel="noopener noreferrer">
          Innpact Solutions
        </a>
      </div>

      {/* Transient hint shown when the VR button can't start a session
          (no WebXR, missing iframe control, etc.). */}
      {vrHint && (
        <div
          role="status"
          style={{
            position: 'fixed',
            top: 72,
            right: 16,
            maxWidth: 320,
            zIndex: 10400,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(20,20,28,0.94)',
            color: '#fff',
            font: '500 12px/1.4 system-ui, -apple-system, "Segoe UI", sans-serif',
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {vrHint}
        </div>
      )}

      {/* ── Kiosk PIN dialog ─────────────────────────────────────────── */}
      {pinOpen && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setPinOpen(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 11000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            style={{
              minWidth: 280, maxWidth: 360,
              background: 'rgba(20,20,28,0.98)',
              border: '1px solid rgba(255,215,0,0.35)',
              borderRadius: 12,
              padding: '20px 22px',
              boxShadow: '0 18px 48px rgba(0,0,0,0.5)',
              color: '#fff',
              font: '500 14px/1.4 system-ui, -apple-system, "Segoe UI", sans-serif',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: '#ffd166' }}>
              {kioskActive ? 'Exit kiosk lock' : 'Enter kiosk lock'}
            </div>
            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 14 }}>
              {kioskActive
                ? 'Enter PIN to exit fullscreen kiosk mode.'
                : 'Enter PIN to lock the app in fullscreen kiosk mode.'}
            </div>
            <input
              type="password"
              inputMode="numeric"
              readOnly
              value={pinValue}
              onKeyDown={(e) => { if (e.key === 'Enter') submitPin(); if (e.key === 'Escape') setPinOpen(false); }}
              placeholder="••••"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', borderRadius: 8,
                border: `1px solid ${pinError ? '#ef4444' : 'rgba(255,255,255,0.18)'}`,
                background: 'rgba(255,255,255,0.06)', color: '#fff',
                fontSize: 22, letterSpacing: '0.5em', textAlign: 'center',
                outline: 'none', caretColor: 'transparent',
              }}
            />
            {pinError && (
              <div style={{ color: '#fca5a5', fontSize: 11, marginTop: 6 }}>{pinError}</div>
            )}

            {/* On-screen numeric keypad — primary input on touch kiosks */}
            <div
              style={{
                marginTop: 14,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
              }}
            >
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'].map((k) => {
                const isAction = k === 'clear' || k === 'back';
                const onPress = () => {
                  setPinError('');
                  if (k === 'clear') setPinValue('');
                  else if (k === 'back') setPinValue((v) => v.slice(0, -1));
                  else setPinValue((v) => (v.length >= 8 ? v : v + k));
                };
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={onPress}
                    style={{
                      padding: '14px 0',
                      borderRadius: 10,
                      fontSize: isAction ? 12 : 20,
                      fontWeight: 700,
                      background: isAction ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.10)',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.14)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      letterSpacing: isAction ? '0.05em' : 0,
                      textTransform: isAction ? 'uppercase' : 'none',
                    }}
                  >
                    {k === 'back' ? '⌫' : k === 'clear' ? 'C' : k}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setPinOpen(false)}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 13,
                  background: 'transparent', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                type="button"
                onClick={submitPin}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  background: '#ffd166', color: '#1a1a1a',
                  border: 'none', cursor: 'pointer',
                }}
              >{kioskActive ? 'Unlock' : 'Lock'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TempleHeader;
